import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SolarScene from "@/components/SolarScene";
import StorageSection from "@/components/StorageSection";
import HomeownerCalculator from "@/components/HomeownerCalculator";
import HomeownerQuoteForm from "@/components/HomeownerQuoteForm";
import { useSite } from "@/context/SiteContext";
import { SERVICES } from "@/lib/services";

const HOME_IDS = ["home", "powerwall", "roofing", "solar-roof", "insulation", "hvac", "ev", "care"];

const lineVariants = {
  hidden: { y: "112%" },
  show: (i) => ({ y: 0, transition: { delay: 0.1 + i * 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] } }),
};

const JOURNEY = [
  { title: "Confirm your home", body: "Enter your address and shape the layout from your bill and roof details." },
  { title: "See the concept", body: "Mark a roof plane on your own photo or explore the 3D concept with your dimensions." },
  { title: "Set backup priorities", body: "Choose what matters during an outage, and explore Tesla Powerwall as an optional next step." },
  { title: "Request your assessment", body: "Contact, consent, and a saved reference for the whole conversation." },
];

export default function Homeowners() {
  const { config, openLead } = useSite();
  const services = SERVICES.filter((s) => HOME_IDS.includes(s.id) && config.enabledServices.includes(s.id));
  return (
    <>
      <Seo
        title="Home solar, Powerwall & roof planning | Solar Black Belt"
        description="See solar on your own roof, explore Tesla Powerwall backup priorities, and request a home energy assessment built around how your household uses power."
      />
      <section className="hero" style={{ paddingBottom: 26 }}>
        <div className="wrap">
          <div className="hero-grid" style={{ gridTemplateColumns: "1.15fr 1fr" }}>
            <div className="hero-copy">
              <div className="badge">
                <span className="dot" /> FOR HOMEOWNERS
              </div>
              <h1 data-testid="homeowners-heading">
                <span className="hero-line">
                  <motion.span custom={0} variants={lineVariants} initial="hidden" animate="show">Your home,</motion.span>
                </span>
                <span className="hero-line">
                  <motion.span custom={1} variants={lineVariants} initial="hidden" animate="show"><em style={{ fontStyle: "normal", color: "var(--teal)" }}>your power.</em></motion.span>
                </span>
              </h1>
              <p style={{ marginTop: 22 }}>
                Explore a rooftop concept on your own house, understand your bill, and keep the lights that matter on when the grid goes down.
              </p>
              <div style={{ display: "flex", gap: 14, marginTop: 26, flexWrap: "wrap" }}>
                <button className="btn lime" data-testid="homeowners-cta" onClick={() => window.dispatchEvent(new CustomEvent("homeowner-quote-prefill", { detail: {} }))}>
                  Get a quote <span>↗</span>
                </button>
                <Link className="btn light" to="/roof-visualizer">See your roof first ↗</Link>
                <Link className="btn light" to="/projects">Jobs we've done ↗</Link>
              </div>
              <p className="micro" style={{ marginTop: 14 }}>
                We specialize in commercial installations — for your home, we'll confirm availability in your area during your quote.
              </p>
            </div>
            <div className="hero-art" style={{ minHeight: 380 }}>
              <div className="scene-label"><span className="dot" /> HOME ENERGY CONCEPT · DRAG TO ROTATE</div>
              <SolarScene kind="home" testId="homeowners-scene" />
              <div className="floating two">
                <span>✦ THE NEXT STEP</span>
                <strong>See what fits.</strong>
                <span className="muted">Your roof. Your household.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 54 }}>
        <div className="wrap">
          <div className="section-heading">
            <div>
              <span className="eyebrow">THE HOME JOURNEY</span>
              <h2>From address to reference number.</h2>
            </div>
            <p>Explore first. Nothing is gated until you choose to request an assessment.</p>
          </div>
          <div className="journey" data-testid="homeowners-journey">
            {JOURNEY.map((j, i) => (
              <Reveal key={j.title} delay={i * 0.08}>
                <div className="journey-step">
                  <span className="num">{i + 1}</span>
                  <b>{j.title}</b>
                  <p>{j.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--mint)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
        <div className="wrap">
          <div className="section-heading">
            <div>
              <span className="eyebrow">FOR THE WAY YOU LIVE</span>
              <h2>Everything your house might need.</h2>
            </div>
            <p>Solar, storage, roofing, comfort and charging — explored in one place.</p>
          </div>
          <div className="cards-grid" data-testid="homeowners-services">
            {services.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05}>
                <div className="service-card" style={{ height: "100%" }}>
                  <span className="icon" aria-hidden="true">{s.icon}</span>
                  <h3>{s.name}</h3>
                  <p>{s.description}</p>
                  <Link className="go" to={`/services/${s.id}`} data-testid={`homeowners-link-${s.id}`}>
                    Explore {s.name.toLowerCase()} ↗
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section storage">
        <div className="wrap">
          <StorageSection onPlanStorage={() => window.dispatchEvent(new CustomEvent("homeowner-quote-prefill", { detail: { priority: "Backup power" } }))} />
        </div>
      </section>

      <HomeownerCalculator onQuote={(bill) => window.dispatchEvent(new CustomEvent("homeowner-quote-prefill", { detail: { bill } }))} />
      <HomeownerQuoteForm />
    </>
  );
}
